import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, ArrowUpRight, ArrowDownRight, CreditCard, Star, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const WalletMetrics = () => {
  // Mock data - in a real app, this would come from an API
  const walletData = [
    { month: "Jan", balance: 160000, subscriptions: 12000, regularFees: 22500, premiumFees: 5000 },
    { month: "Feb", balance: 172000, subscriptions: 14500, regularFees: 24300, premiumFees: 5800 },
    { month: "Mar", balance: 175000, subscriptions: 16800, regularFees: 24500, premiumFees: 6200 },
    { month: "Apr", balance: 190000, subscriptions: 19200, regularFees: 25900, premiumFees: 7300 },
    { month: "May", balance: 205000, subscriptions: 22500, regularFees: 27200, premiumFees: 9100 },
    { month: "Jun", balance: 215000, subscriptions: 25800, regularFees: 29700, premiumFees: 10200 },
    { month: "Jul", balance: 232000, subscriptions: 29400, regularFees: 31800, premiumFees: 11600 },
    { month: "Aug", balance: 246000, subscriptions: 32800, regularFees: 34500, premiumFees: 13000 }
  ];

  const transactions = [
    { id: 1, date: "2023-08-15", type: "Deposit", amount: 12500, status: "completed" },
    { id: 2, date: "2023-08-12", type: "Withdrawal", amount: 5200, status: "completed" },
    { id: 3, date: "2023-08-10", type: "Deposit", amount: 8700, status: "completed" },
    { id: 4, date: "2023-08-05", type: "Withdrawal", amount: 3800, status: "completed" },
    { id: 5, date: "2023-08-01", type: "Deposit", amount: 15000, status: "completed" }
  ];

  const subscriptionData = {
    totalSubscribers: 3315,
    monthlyRevenue: "€32,818.50",
    growthRate: "12.2%",
    churnRate: "3.1%",
    averageSubscriptionLength: "8.4 months"
  };

  // Service fees breakdown data
  const serviceFeeData = [
    { name: "Regular Caregivers (17%)", value: 34500, color: "#8B5CF6" },
    { name: "Premium Caregivers (14%)", value: 13000, color: "#10B981" },
  ];

  const COLORS = ["#8B5CF6", "#10B981"];

  // Calculate totals
  const totalRegularFees = walletData.reduce((sum, item) => sum + item.regularFees, 0);
  const totalPremiumFees = walletData.reduce((sum, item) => sum + item.premiumFees, 0);
  const totalFees = totalRegularFees + totalPremiumFees;

  // Custom formatter for tooltip
  const customTooltipFormatter = (value) => [`$${value.toLocaleString()}`];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="col-span-2 md:col-span-1">
        <CardHeader>
          <CardTitle>Total Wallet Balance</CardTitle>
          <CardDescription>Historical growth in wallet balance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ChartContainer
              config={{
                balance: { color: "#8B5CF6" },
                subscriptions: { color: "#10B981" }
              }}
            >
              <BarChart data={walletData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Bar 
                  dataKey="balance" 
                  fill="#8B5CF6" 
                  radius={[4, 4, 0, 0]} 
                  name="Balance"
                />
                <Bar 
                  dataKey="subscriptions" 
                  fill="#10B981" 
                  radius={[4, 4, 0, 0]} 
                  name="Subscription Revenue"
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => [
                        `$${value.toLocaleString()}`, 
                        name === "balance" ? "Balance" : "Subscription Revenue"
                      ]}
                    />
                  }
                />
              </BarChart>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="col-span-2 md:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Recent Transactions</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-sm font-normal text-muted-foreground">
                    <Info className="h-4 w-4" />
                    Service Fees
                  </div>
                </TooltipTrigger>
                <TooltipContent className="w-80">
                  <p>Spheroo charges 17% for regular transactions and 14% for premium caregiver transactions</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>Latest wallet activities</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{new Date(transaction.date).toLocaleDateString()}</TableCell>
                  <TableCell className="flex items-center gap-2">
                    {transaction.type === "Deposit" ? (
                      <>
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                          <ArrowUpRight className="h-3 w-3 text-green-600" />
                        </span>
                        <span>Deposit</span>
                      </>
                    ) : (
                      <>
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
                          <ArrowDownRight className="h-3 w-3 text-red-600" />
                        </span>
                        <span>Withdrawal</span>
                      </>
                    )}
                  </TableCell>
                  <TableCell className={`text-right ${transaction.type === "Deposit" ? "text-green-600" : "text-red-600"}`}>
                    {transaction.type === "Deposit" ? "+" : "-"}${transaction.amount.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New card for Service Fees */}
      <Card className="col-span-2 md:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-violet-500" />
            Platform Service Fees
          </CardTitle>
          <CardDescription>
            Regular: 17% | Premium: 14% of transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-[200px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={serviceFeeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {serviceFeeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              <div className="bg-violet-50 p-4 rounded-lg">
                <div className="text-xs text-violet-600 font-medium">TOTAL SERVICE FEES</div>
                <div className="text-2xl font-bold mt-1">${totalFees.toLocaleString()}</div>
                <div className="text-xs text-green-600 mt-1">+8.3% from last month</div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 bg-violet-500 rounded-sm"></div>
                    <span className="text-sm">Regular (17%)</span>
                  </div>
                  <span className="text-sm font-medium">${totalRegularFees.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 bg-emerald-500 rounded-sm"></div>
                    <span className="text-sm">Premium (14%)</span>
                  </div>
                  <span className="text-sm font-medium">${totalPremiumFees.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Service Fees chart */}
      <Card className="col-span-2 md:col-span-1">
        <CardHeader>
          <CardTitle>Monthly Service Fees</CardTitle>
          <CardDescription>Revenue from transaction fees</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ChartContainer
              config={{
                regularFees: { color: "#8B5CF6" },
                premiumFees: { color: "#10B981" }
              }}
            >
              <BarChart data={walletData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Bar 
                  dataKey="regularFees" 
                  fill="#8B5CF6" 
                  radius={[4, 4, 0, 0]} 
                  name="Regular Fees (17%)"
                  stackId="a"
                />
                <Bar 
                  dataKey="premiumFees" 
                  fill="#10B981" 
                  radius={[4, 4, 0, 0]} 
                  name="Premium Fees (14%)"
                  stackId="a"
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => [
                        `$${value.toLocaleString()}`, 
                        name === "regularFees" ? "Regular Fees (17%)" : "Premium Fees (14%)"
                      ]}
                    />
                  }
                />
                <Legend />
              </BarChart>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-500" />
            Professional Premium Subscriptions
          </CardTitle>
          <CardDescription>€9.90 monthly subscription revenue from professional caregivers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <Card className="bg-blue-50">
              <CardContent className="p-4">
                <div className="text-xs text-blue-500 font-medium">SUBSCRIBERS</div>
                <div className="text-2xl font-bold mt-1">{subscriptionData.totalSubscribers}</div>
                <div className="text-xs text-green-500 mt-1">+{subscriptionData.growthRate} growth</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4">
                <div className="text-xs text-green-500 font-medium">MONTHLY REVENUE</div>
                <div className="text-2xl font-bold mt-1">{subscriptionData.monthlyRevenue}</div>
                <div className="text-xs text-green-500 mt-1">+{subscriptionData.growthRate} from last month</div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50">
              <CardContent className="p-4">
                <div className="text-xs text-amber-500 font-medium">AVG SUBSCRIPTION</div>
                <div className="text-2xl font-bold mt-1">{subscriptionData.averageSubscriptionLength}</div>
                <div className="text-xs text-muted-foreground mt-1">Average length</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="p-4">
                <div className="text-xs text-red-500 font-medium">CHURN RATE</div>
                <div className="text-2xl font-bold mt-1">{subscriptionData.churnRate}</div>
                <div className="text-xs text-green-500 mt-1">-0.8% from last month</div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50">
              <CardContent className="p-4">
                <div className="text-xs text-purple-500 font-medium">RESOURCE USAGE</div>
                <div className="text-2xl font-bold mt-1">87%</div>
                <div className="text-xs text-green-500 mt-1">+12% engagement</div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              Top Earned Badges
            </h4>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">Communication Skills</Badge>
              <Badge className="bg-green-100 text-green-700 hover:bg-green-200">Empathy</Badge>
              <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200">Crisis Management</Badge>
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200">Conflict Resolution</Badge>
              <Badge className="bg-red-100 text-red-700 hover:bg-red-200">Time Management</Badge>
              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200">Leadership</Badge>
              <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-200">Stress Management</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

